import { createContext, useContext, useEffect, useState } from 'react'

import type { createFeatureRelayClient } from '@square/feature-relay-web-sdk'

import { getClient } from '../lib/launchDarkly'

type FeatureFlagClient = ReturnType<typeof createFeatureRelayClient>

interface FeatureFlagContextProps {
  client?: FeatureFlagClient
  isLoading: boolean
  error?: Error
}

const FeatureFlagContext = createContext<FeatureFlagContextProps>({
  isLoading: true,
})

export const useFeatureFlagsContext = () => {
  const context = useContext(FeatureFlagContext)

  if (!context) {
    throw new Error('useFeatureFlagsContext must be used within FeatureFlagProvider')
  }

  return context
}

interface FeatureFlagProviderProps {
  children: React.ReactNode
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  const [client, setClient] = useState<FeatureFlagClient>()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error>()

  useEffect(() => {
    const featureClient = getClient()

    featureClient
      ?.loadFlags()
      .then(() => {
        setClient(featureClient)
        setIsLoading(false)
      })
      .catch(err => {
        setError(err instanceof Error ? err : new Error('Failed to initialize feature flags'))
        setIsLoading(false)
      })
  }, [])

  return <FeatureFlagContext.Provider value={{ client, isLoading, error }}>{children}</FeatureFlagContext.Provider>
}
